'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';

interface GeolocationResponse {
  country: string;
  ip: string | null;
  source: string;
  isGDPR: boolean;
}

interface GeolocationContextType {
  country: string;
  isGDPR: boolean;
  isLoaded: boolean;
}

const GeolocationContext = createContext<GeolocationContextType>({
  country: '',
  isGDPR: false,
  isLoaded: false,
});

export const useGeolocation = () => useContext(GeolocationContext);

export const GeolocationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [country, setCountry] = useState('');
  const [isGDPR, setIsGDPR] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const detectRegion = async () => {
      try {
        const response = await fetch('/api/geolocation');
        if (response.ok) {
          const data: GeolocationResponse = await response.json();
          setCountry(data.country);
          setIsGDPR(data.isGDPR);
        } else {
          setCountry('US');
          setIsGDPR(false);
        }
      } catch (error) {
        console.warn('Geolocation detection failed, defaulting to US:', error);
        setCountry('US');
        setIsGDPR(false);
      } finally {
        setIsLoaded(true);
      }
    };

    // Defer detection until browser is idle to avoid blocking critical rendering
    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(() => detectRegion(), {
        timeout: 2000,
      });
      return () => window.cancelIdleCallback(idleId);
    } else {
      const timeoutId = setTimeout(detectRegion, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, []);

  return (
    <GeolocationContext.Provider value={{ country, isGDPR, isLoaded }}>
      {children}
    </GeolocationContext.Provider>
  );
};
